import { ReactElement, useEffect, useState } from "react";
import { IHeatMapChartConfig, IPoint } from "./types";
import Chart from "./chart";
import { useChartDimensions, useUniqueId } from "./chartUtils";
import * as d3 from "d3";
import { IDictionary } from "../../types";
import { useTheme } from "@mui/material";
import HtmlTooltip from "./tooltip";
import { formatNumber } from "../../system/utils";
import AxisHorizontal from "./AxisHorizontal";

interface IChartProps {
  chartConfig: IHeatMapChartConfig;
}

const HeatMapChart = ({ chartConfig }: IChartProps): ReactElement => {
  const theme = useTheme();
  const [ data, setData ] = useState<IDictionary<any>>({});
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: chartConfig.height ?? undefined,
    width: chartConfig.width ?? undefined,
    marginBottom: 40,
    marginLeft: Math.max(...chartConfig.series.map(x => x.name.length)) * 8 + 10,
    marginRight: 50,
    marginTop: chartConfig.showLegend  ? 20 : 10
  });

  useEffect(() => {
    const newData: IDictionary<any> = {};

    chartConfig.series.forEach(s => {

      (s.value as IPoint[]).forEach(p => {
        if (!newData[p.x]) {
          newData[p.x] = { [s.name]: p.y };
        } else {
          newData[p.x][s.name] = p.y;
        }
      });

    });

    setData(newData);
  }, [chartConfig]);

  const chartID = useUniqueId("heatmap-chart");

  const keys = Object.keys(data);

  if (keys.length === 0) {
    return <div/>;
  }

  const xScale = d3.scaleBand()
    .domain(chartConfig.xAxis.categories)
    .range([0, dimensions.boundedWidth]);

  const colors: any = {};
  chartConfig.series.forEach(s => {
    colors[s.name] = {
      palette: d3.interpolate(theme.palette.background.paper, s.color),
      max: Math.max(...(s.value as IPoint[]).map(p => p.y))
    };
  });

  const seriesH = Math.round(dimensions.boundedHeight / chartConfig.series.length);
  const seriesW = xScale.bandwidth();

  return (
    <div ref={ref} >
      <Chart dimensions={dimensions} >

        <AxisHorizontal
          chartID={chartID}
          dimensions={dimensions}
          scale={xScale}
          label={chartConfig.xAxis.name}
          ticks={keys}
          formatTick={chartConfig.xAxis.formatTick}
        />

        { keys.map((key, index) => {
          const keyC = `${chartID}-series-${index}`;
          const x = Math.round(xScale(key) ?? 0);

          return(
            <g key={keyC}>
              { chartConfig.series.map((s, vIndex) => {

                if (!data[key][s.name]) {
                  return(
                    <rect key={`${keyC}-rect-${vIndex}`}
                      x={x}
                      y={Math.round(seriesH * vIndex)}
                      rx={2}
                      ry={2}
                      height={Math.max(0, seriesH - 2)}
                      width={Math.max(0, seriesW - 2)}
                      fill="transparent"
                      stroke="gray"
                      strokeWidth={1}
                    />
                  );
                }

                const fill = colors[s.name].palette(data[key][s.name] / colors[s.name].max);

                return(
                  <HtmlTooltip
                    key={`${keyC}-rect-${vIndex}`}
                    title={
                      <div>
                        <strong>{key}</strong>
                        <div className="display-flex-row align-items-center" >
                          <div style={{ background: s.color, width: 10, height: 10, marginRight: 10 }} />
                          <strong className="flex-grow-1">{s.name}</strong>
                          <strong className="flex-grow-0">
                            {formatNumber(data[key][s.name], 2)}
                            {s.unit && <sub>{`[${s.unit}]`}</sub>}
                          </strong>
                        </div>
                      </div>
                    }>
                    <rect
                      x={x}
                      y={Math.round(seriesH * vIndex)}
                      rx={2}
                      ry={2}
                      height={Math.max(0, seriesH - 2)}
                      width={Math.max(0, seriesW - 2)}
                      fill={fill}
                      stroke="gray"
                      strokeWidth={1}
                    />
                  </HtmlTooltip>
                );
              })
              }
            </g>
          );
        })}

        { chartConfig.series.map((s, vIndex) => {
          return(
            <text key={`${chartID}-series-${vIndex}`} y={Math.round(seriesH * vIndex) + seriesH / 2} x={-dimensions.marginLeft} fill={theme.palette.text.primary}>{s.name}</text>
          );
        })}
        <g />
      </Chart>
    </div>);
};

export default HeatMapChart;
